import type { PageRow, TableColumn, TableWidget as TableWidgetSpec } from '@developerz/domain';
import { createMemo, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { formatCell } from '../../lib/widget-format';
import { DataTable, type DataTableColumn } from '../DataTable';

// `table` presenter (arc 101 slice 09) — paginated, sortable rows. Columns are the
// spec's when given, else auto-mapped from the first row's keys ("absent columns =
// auto-map result keys", per the grammar). Cells format per their column hint.
function resolveColumns(spec: TableWidgetSpec, rows: readonly PageRow[]): readonly TableColumn[] {
  if (spec.columns && spec.columns.length > 0) return spec.columns;
  const first = rows[0];
  return first ? Object.keys(first).map((key) => ({ key, label: key })) : [];
}

export function TableWidget(props: {
  spec: TableWidgetSpec;
  rows: readonly PageRow[];
}): JSX.Element {
  const columns = createMemo((): readonly DataTableColumn<PageRow>[] =>
    resolveColumns(props.spec, props.rows).map((c) => ({
      key: c.key,
      header: c.label,
      cell: (row) => formatCell(row[c.key], c.format),
    })),
  );
  // A query row carries no identity of its own; its position in the page is the
  // one address it has.
  const position = createMemo(() => new Map(props.rows.map((row, i) => [row, String(i)])));
  return (
    <Show when={props.rows.length > 0} fallback={<p class="widget__empty">{t('widgets.empty')}</p>}>
      <DataTable
        label={props.spec.title ?? t('widgets.table_label')}
        columns={columns()}
        rows={props.rows}
        rowKey={(row) => position().get(row) ?? ''}
      />
    </Show>
  );
}
